import { useState, useCallback, useMemo } from "react";

import { usePulseApiToken } from "@/features/auth/hooks/useApiToken";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { copyToClipboard } from "@/lib/clipboard";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import { Button } from "@/components/ui/button";
import { useSearch } from "@tanstack/react-router";
import { CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTokenManagement } from "@/hooks/useTokenManagement";
import { MONITORING_TYPES } from "@/constants/monitoring";
import { useCreatePulse } from "../../api/pulse.queries";
import { Spinner } from "@/components/ui/spinner";

export default function PulseCreateContainer() {
  const { mode } = useSearch({ from: "/pulse/create" });
  const [showResults, setShowResults] = useState(false);
  const [, setIsLaunching] = useState(false);
  const [hasLaunched, setHasLaunched] = useState(true);
  const { apiToken, isPending: isApiTokenLoading } = usePulseApiToken();
  const { user } = useAuthStore();

  // Token management with dropdown functionality
  const {
    selectedToken,
    effectiveToken,
    handleTokenSelect,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken,
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => true,
    monitoringType: "pulse",
  });

  const handleCopyCode = useCallback(() => {
    copyToClipboard("{}");
    toast.success("Copied to clipboard");
  }, []);

  const handleLaunch = useCallback(async () => {
    if (!showResults) {
      setIsLaunching(true);
      setTimeout(() => {
        setShowResults(true);
        setHasLaunched(true);
        setIsLaunching(false);
      }, 300);
    }
  }, [showResults]);

  const handlePanelResize = useCallback((sizes: number[]) => {
    const rightPanelSize = sizes[1];
    if (rightPanelSize < 5) {
      setShowResults(false);
    }
  }, []);

  const apiEndpoint = "";

  const handleExportJSON = useCallback(() => {
    toast.info("JSON export coming soon");
  }, []);

  const handleExportCSV = useCallback(() => {
    toast.info("CSV export coming soon");
  }, []);

  const [pulseName, setPulseName] = useState("");
  const [postUrl, setPostUrl] = useState("");

  const { mutate: createPulseMutation, isPending } = useCreatePulse();

  const handleCreatePulse = useCallback(async () => {
    createPulseMutation({
      pulse_name: pulseName,
      post_url: postUrl,
      platform: "twitter",
      max_wait_seconds: 1200,
    });
  }, [createPulseMutation, pulseName, postUrl]);

  const content = useMemo(
    () => (
      <div className="flex h-full min-h-0 flex-col overflow-x-hidden">
        <div
          className="flex-1 overflow-x-hidden overflow-y-auto"
          style={{
            transform: "translateZ(0)",
            scrollBehavior: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <div className="flex min-h-full max-w-[940px] flex-col gap-5 pr-3">
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div className="flex w-full flex-col gap-1">
                <CardTitle className="pb-5 text-[28px] leading-[36px] -tracking-wide text-neutral-950">
                  {mode === "edit" ? "Edit pulse" : "Create a new pulse"}
                </CardTitle>
                <div className="flex w-full flex-col gap-5">
                  <div className="flex w-full flex-col gap-1">
                    <Label
                      htmlFor="pulse-name"
                      className="text-sm font-bold text-neutral-800"
                    >
                      Pulse Name <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="pulse-name"
                      type="text"
                      variant="filled"
                      value={pulseName}
                      onChange={(e) => setPulseName(e.target.value)}
                      placeholder="24 Oras Pulse"
                      required
                      className="w-full truncate"
                    />
                  </div>
                  <div className="flex w-full flex-row gap-5">
                    <div className="flex w-full flex-col gap-1">
                      <Label
                        htmlFor="channel-post-url"
                        className="text-sm font-bold text-neutral-800"
                      >
                        Channel/Post URL <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="channel-post-url"
                        type="text"
                        variant="filled"
                        value={postUrl}
                        onChange={(e) => setPostUrl(e.target.value)}
                        placeholder="https://www.instagram.com/p/CirjMgounhA/"
                        required
                        className="w-full truncate"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    [mode, pulseName, postUrl, setPulseName, setPostUrl],
  );

  const bottomContent = useMemo(
    () => (
      <div className="bg-brand-50 flex h-[72px] w-full flex-row items-end justify-start gap-3 py-2">
        <Button
          variant="default"
          icon
          className="flex h-[48px] w-[240px] gap-2 hover:scale-none"
          onClick={handleCreatePulse}
          disabled={
            isPending || !effectiveToken || !pulseName.trim() || !postUrl.trim()
          }
        >
          {isPending ? (
            <div className="flex h-full items-center justify-center text-neutral-900">
              <Spinner size="sm" className="stroke-neutral-900!" />
            </div>
          ) : (
            <>{mode === "edit" ? "Update pulse" : "Save pulse"}</>
          )}
        </Button>
      </div>
    ),
    [handleCreatePulse, isPending, effectiveToken, pulseName, postUrl, mode],
  );

  return (
    <TwoColumnLayout
      data={[]}
      isLoading={false}
      error={null}
      responseJSON={""}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Results"
      showResults={showResults}
      onShowResults={setShowResults}
      onLaunch={handleLaunch}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={hasLaunched}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      onPanelResize={handlePanelResize}
      bottomContent={bottomContent}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={MONITORING_TYPES.PULSE}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      {content}
    </TwoColumnLayout>
  );
}
